import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/Layout/PageHeader.jsx';
import { Button, FormCard } from '../../../components/ui/FormComponents.jsx';
import { useToast } from '../../../hooks/useKeyboardShortcuts.js';
import Swal from 'sweetalert2';
import { Save, X, Building2, Phone, TrendingUp, Bell, DollarSign, MapPin } from '../../../components/icons/index.jsx';
import {
  createClienteCompleto,
  getLocalities,
  getProvinces,
  searchDuplicates,
  validateCUIT,
} from '../../../services/clients.js';

// Importar componentes de pestañas
import GeneralTab from './GeneralTab.jsx';
import ContactoTab from './ContactoTab.jsx';
import ComercialTab from './ComercialTab.jsx';
import NotificacionesTab from './NotificacionesTab.jsx';
import TarifasTab from './TarifasTab.jsx';
import DomiciliosTab from './DomiciliosTab.jsx';

// Datos iniciales para la pestaña Notificaciones
const INITIAL_NOTIFICACIONES = {
  destinosGenerales: { emails: '', whatsapp: '' },
  avisosPorEstado: {},
  aplicarATodos: false,
  noEnviarFueraHorario: false,
  horarioDesde: '',
  horarioHasta: '',
};

// Datos iniciales para la pestaña Tarifas
const INITIAL_TARIFAS = {
  tarifaBaseId: '',
  descuentoPct: '',
  recargoPct: '',
  requiereConfirmacion: false,
  prioridadDespacho: false,
  prioridadEntrega: false,
  observaciones: '',
};


// Datos iniciales para la pestaña General
const INITIAL_GENERAL = {
  razonSocial: '',
  nombreFantasia: '',
  tipoDocumento: '',
  cuit: '',
  condicionIVA: 'Responsable Inscripto',
  categoriaCliente: 'Minorista',
  codigo: '',
  tipoCliente: '',
  estado: 'ACTIVO',
  motivoEstado: '',
  direccion: '',
  pisoDepto: '',
  pais: 'Argentina',
  provincia: '',
  localidad: '',
  codigoLocalidad: '',
  codigoPostal: '',
  cp: '',
  lat: '',
  lng: '',
  observaciones: '',
};

// Datos iniciales para la pestaña Contacto
const INITIAL_CONTACTOS = [];

const INITIAL_DOMICILIOS = [];

// Datos iniciales para la pestaña Comercial
const INITIAL_COMERCIAL = {
  empresa: '',
  sucursal: '',
  vendedor: '',
  listaPrecio: 'General',
  condicionVenta: 'Contado',
  condicionPago: 'CONTADO',
  plazoPago: '',
  metodoPago: '',
  limiteCredito: '',
  diasVencimiento: '',
  toleranciaVencidos: '',
  prioridadOperativa: 'NORMAL',
  bloqueoPorDeuda: false,
  umbralDeuda: '',
  slaDespachoHs: '',
  slaEntregaHs: '',
  zonaReparto: '',
  observacionesComerciales: '',
  activo: true,
  fechaAlta: new Date().toISOString().slice(0, 10),
};

export default function NuevoCliente() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  
  // Estados del formulario
  const [generalData, setGeneralData] = useState(INITIAL_GENERAL);
  const [notificacionesData, setNotificacionesData] = useState(INITIAL_NOTIFICACIONES);
  const [tarifasData, setTarifasData] = useState(INITIAL_TARIFAS);
  const [contactosData, setContactosData] = useState(INITIAL_CONTACTOS);
  const [domiciliosData, setDomiciliosData] = useState(INITIAL_DOMICILIOS);
  const [comercialData, setComercialData] = useState(INITIAL_COMERCIAL);
  
  // Estado de la pestaña activa
  const [activeTab, setActiveTab] = useState('general');
  
  // Estados de UI
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);
  const [errors, setErrors] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [afipVerified, setAfipVerified] = useState(false); // ✅ Indica si se validó con AFIP
  const [afipBlocked, setAfipBlocked] = useState(false); // ✅ Indica si AFIP devolvió INACTIVO y debe bloquearse
  const saveHandlerRef = useRef(null);

  // Cargar provincias al montar
  useEffect(() => {
    const loadProvinces = async () => {
      try {
        const data = await getProvinces();
        setProvinces(data);
      } catch (error) {
        console.error('Error cargando provincias:', error);
      }
    };
    loadProvinces();
  }, []);

  // Cargar localidades cuando cambia la provincia
  useEffect(() => {
    if (generalData.provincia) {
      const loadLocalities = async () => {
        setIsLoadingLocalities(true);
        try {
          const data = await getLocalities(generalData.provincia);
          setLocalities(data);
        } catch (error) {
          console.error('Error cargando localidades:', error);
        } finally {
          setIsLoadingLocalities(false);
        }
      };
      loadLocalities();
    } else {
      setLocalities([]);
    }
  }, [generalData.provincia]);


   //Maneja el cambio de provincia y resetea la localidad y código de localidad
 
  const handleProvinceChange = (provincia) => {
    setGeneralData(prev => ({ 
      ...prev, 
      provincia, 
      localidad: '',
      codigoLocalidad: ''
    }));
  };

  
    //Validación completa del formulario
    //Verifica todos los campos requeridos de todas las pestañas
 
  const validateForm = () => {
    const newErrors = {};
    
    const requiresArcaVerification = generalData.tipoDocumento === 'CUIT';

    // ⚠️ VALIDACIÓN OBLIGATORIA DE ARCA PARA CUIT
    if (afipBlocked) {
      newErrors.afip = '❌ El contribuyente figura INACTIVO en ARCA y no se puede guardar';
      toast.error('El contribuyente figura INACTIVO en ARCA. No es posible continuar');
      return newErrors;
    }

    if (requiresArcaVerification && !afipVerified) {
      newErrors.afip = '⚠️ Debe verificar el CUIT con ARCA antes de continuar';
      toast.error('Debe consultar ARCA antes de guardar el cliente');
      return newErrors;
    }
    
    // Validar campos requeridos de General
    if (!generalData.razonSocial?.trim()) {
      newErrors.razonSocial = 'La razón social es requerida';
    }
    
    if (!generalData.tipoDocumento) {
      newErrors.tipoDocumento = 'El tipo de documento es requerido';
    }
    
    if (!generalData.cuit?.trim()) {
      newErrors.cuit = 'El número de documento es requerido';
    } else if (generalData.tipoDocumento === 'CUIT' && !validateCUIT(generalData.cuit)) {
      newErrors.cuit = 'CUIT inválido';
    }
    
    if (!generalData.condicionIVA) {
      newErrors.condicionIVA = afipVerified
        ? 'AFIP no devolvió condición IVA. Completala manualmente para poder facturar.'
        : 'La condición de IVA es requerida';
    }
    
    // Nota: categoriaCliente ya no es obligatoria (la BD tiene default)
    
    // Validar contactos
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    contactosData.forEach((contacto, index) => {
      if (contacto.email && !emailRegex.test(contacto.email)) {
        newErrors[`contacto_${index}_email`] = 'Formato de email inválido';
      }
    });
    
    // Validar campos numéricos de Comercial
    if (comercialData.limiteCredito && isNaN(parseFloat(comercialData.limiteCredito))) {
      newErrors.limiteCredito = 'El límite de crédito debe ser un número válido';
    }
    
    if (comercialData.plazoPago && isNaN(parseInt(comercialData.plazoPago))) {
      newErrors.plazoPago = 'El plazo de pago debe ser un número válido';
    }

    if (comercialData.diasVencimiento && isNaN(parseInt(comercialData.diasVencimiento))) {
      newErrors.diasVencimiento = 'Los días de vencimiento deben ser un número válido';
    }

    if (comercialData.toleranciaVencidos && isNaN(parseInt(comercialData.toleranciaVencidos))) {
      newErrors.toleranciaVencidos = 'La tolerancia debe ser un número válido';
    }

    if (comercialData.umbralDeuda && isNaN(parseFloat(comercialData.umbralDeuda))) {
      newErrors.umbralDeuda = 'El umbral de deuda debe ser un número válido';
    }

    if (generalData.estado && generalData.estado !== 'ACTIVO' && !generalData.motivoEstado?.trim()) {
      newErrors.motivoEstado = 'Indicá el motivo del estado';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? true : newErrors;
  };


  const handleSave = async (e) => {

  if (e) e.preventDefault();
  
  const validation = validateForm();
  if (validation !== true) {
    // validation es un objeto con los errores
    const missing = Object.keys(validation).filter(k => validation[k]);

    // Definir campos opcionales que pueden omitirse con confirmación
    const optionalFields = new Set([
      'categoriaCliente',
      'telefono',
      'celular',
      'telefonoAlternativo',
      'emails',
      'direccion',
      'codigoPostal',
      'cp',
      'lat',
      'lng',
      'provincia',
      'provinciaId',
      'localidad',
      'localidadId',
      'plazoPago',
      'limiteCredito',
      'empresa',
      'sucursal',
      'vendedor',
      'tipoCliente',
      'listaPrecio',
      'condicionVenta',
      'condicionPago',
      'metodoPago',
      'diasVencimiento',
      'toleranciaVencidos',
      'prioridadOperativa',
      'bloqueoPorDeuda',
      'umbralDeuda',
      'slaDespachoHs',
      'slaEntregaHs',
      'zonaReparto',
      'observaciones',
      'observacionesComerciales',
      'notasContacto',
      'sitioWeb',
      'horarioAtencion',
      'codigo',
    ]);

    const nonOptionalMissing = missing.filter(m => !optionalFields.has(m));

    if (nonOptionalMissing.length === 0 && missing.length > 0) {
      // Solo faltan campos opcionales: preguntar si desea continuar
      const friendly = missing.map(f => `- ${f}`).join('\n');
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Faltan datos opcionales',
        html: `<p>Faltan campos opcionales que se pueden completar luego:</p><pre style="text-align:left">${friendly}</pre><p>¿Deseas crear el cliente igualmente?</p>`,
        showCancelButton: true,
        confirmButtonText: 'Crear igualmente',
        cancelButtonText: 'Cancelar',
      });

      if (!result.isConfirmed) {
        toast.info('Creación cancelada');
        // Enfocar la primera pestaña relevante
        if (validation.razonSocial || validation.tipoDocumento || validation.cuit || validation.condicionIVA || validation.motivoEstado) {
          setActiveTab('general');
        } else if (validation.emails) {
          setActiveTab('contacto');
        }
        return;
      }
      // Si confirmó, seguimos con la creación
    } else {
      // Hay campos requeridos faltantes: mostrar lista y bloquear
      if (missing.length) {
        const friendly = missing.map(f => `- ${f}`).join('\n');
        await Swal.fire({
          icon: 'error',
          title: 'Faltan datos obligatorios',
          html: `<pre style="text-align:left">${friendly}</pre>`,
          confirmButtonText: 'Entendido',
        });
      }

      toast.error('Por favor, corrige los errores en el formulario');
      // Cambiar a la pestaña donde esté el primer error conocido
      if (validation.razonSocial || validation.tipoDocumento || validation.cuit || validation.condicionIVA || validation.motivoEstado) {
        setActiveTab('general');
      } else if (Object.keys(validation).some(key => key.startsWith('contacto_'))) {
        setActiveTab('contacto');
      } else if (validation.limiteCredito || validation.plazoPago) {
        setActiveTab('comercial');
      }
      return;
    }
  }
  
  setIsLoading(true);
  
  try {
    const duplicates = await searchDuplicates(generalData.cuit, generalData.razonSocial);
    if (duplicates.length > 0) {
      toast.warning('Ya existe un cliente con estos datos');
      setIsLoading(false);
      return;
    }
    
    const generalPayload = { ...generalData };
    delete generalPayload.codigo;
    delete generalPayload.tipoCliente;
    const clientePayload = {
      ...generalPayload,
      ...comercialData,
      // Asegurar que se envíen los IDs normalizados
      provinciaId: generalData.provincia || null,
      localidadId: generalData.localidad || null,
      codigoLocalidad: generalData.codigoLocalidad || null,
      categoriaCliente: generalData.categoriaCliente || 'Minorista',
      estado: generalData.estado || 'ACTIVO',
      motivoEstado: generalData.estado !== 'ACTIVO' ? generalData.motivoEstado : null,
      observaciones: [
        generalData.observaciones?.trim(),
        generalData.localidadManual?.trim() ? `Localidad manual: ${generalData.localidadManual.trim()}` : '',
      ].filter(Boolean).join('\n'),
    };

    const nuevoCliente = {
      cliente: clientePayload,
      contactos: contactosData,
      domicilios: domiciliosData,
      notificacionesConfig: notificacionesData,
      tarifasConfig: tarifasData,
    };
    
    // Crear cliente en la API
    const response = await createClienteCompleto(nuevoCliente);
    
    if (response.success) {
      toast.success('Cliente creado exitosamente');
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: {
            refreshClientId: response.id,
            autoSelectClientId: response.id,
          },
        });
      } else {
        navigate('/clientes');
      }
    } else {
      toast.error(response.message || 'Error al crear el cliente');
    }
  } catch (error) {
    console.error('Error creando cliente:', error);
    toast.error('Error interno del servidor');
  } finally {
    setIsLoading(false);
  }
};


  useEffect(() => {
    saveHandlerRef.current = handleSave;
  });

  // F5 es el único atajo de creación de esta pantalla y evita la recarga del navegador.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'F5') return;
      event.preventDefault();
      saveHandlerRef.current?.();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCancel = () => {
    navigate(location.state?.returnTo || '/clientes');
  };

  const renderTabContent = () => {
    if (activeTab === 'general') {
      return (
        <GeneralTab
          data={generalData}
          onChange={setGeneralData}
          errors={errors}
          provinces={provinces}
          localities={localities}
          onProvinceChange={handleProvinceChange}
          isLoadingLocalities={isLoadingLocalities}
          toast={toast}
          afipVerified={afipVerified}
          setAfipVerified={setAfipVerified}
          afipBlocked={afipBlocked}
          setAfipBlocked={setAfipBlocked}
        />
      );
    }
    if (activeTab === 'contacto') {
      return (
        <ContactoTab
          data={contactosData}
          onChange={setContactosData}
          errors={errors}
        />
      );
    }
    if (activeTab === 'domicilios') {
      return (
        <DomiciliosTab
          data={domiciliosData}
          onChange={setDomiciliosData}
          provinces={provinces}
        />
      );
    }
    if (activeTab === 'comercial') {
      return (
        <ComercialTab
          data={comercialData}
          onChange={setComercialData}
          errors={errors}
        />
      );
    }
    if (activeTab === 'notificaciones') {
      return (
        <NotificacionesTab
          data={notificacionesData}
          onChange={setNotificacionesData}
        />
      );
    }
    if (activeTab === 'tarifas') {
      return (
        <TarifasTab
          data={tarifasData}
          onChange={setTarifasData}
        />
      );
    }
    return null;
  };

  return (
    <>
      <PageHeader
        title="Nuevo Cliente"
        subtitle="Alta de clientes para facturación y asignación de servicios"
        actions={
          <>
            <Button type="button" variant="secondary" onClick={handleCancel}>
              <X size={16} className="mr-1" />
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="button"
              onClick={handleSave}
                disabled={isLoading || afipBlocked}
            >
              <Save size={16} className="mr-1" />
              {isLoading ? 'Guardando...' : 'Crear Cliente'}
            </Button>
          </>
        }
      />

      <div className="space-y-6">
        {/* Navegación de pestañas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('general')}
              aria-current={activeTab === 'general' ? 'page' : undefined}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === 'general'
                  ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Building2 size={18} />
              General
            </button>
            <button
              onClick={() => setActiveTab('contacto')}
              aria-current={activeTab === 'contacto' ? 'page' : undefined}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === 'contacto'
                  ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Phone size={18} />
              Contacto
            </button>
            <button
              onClick={() => setActiveTab('domicilios')}
              aria-current={activeTab === 'domicilios' ? 'page' : undefined}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === 'domicilios'
                  ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <MapPin size={18} />
              Domicilios
            </button>
            <button
              onClick={() => setActiveTab('comercial')}
              aria-current={activeTab === 'comercial' ? 'page' : undefined}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === 'comercial'
                  ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <TrendingUp size={18} />
              Comercial
            </button>
            <button
              onClick={() => setActiveTab('notificaciones')}
              aria-current={activeTab === 'notificaciones' ? 'page' : undefined}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === 'notificaciones'
                  ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Bell size={18} />
              Notificaciones
            </button>
            <button
              onClick={() => setActiveTab('tarifas')}
              aria-current={activeTab === 'tarifas' ? 'page' : undefined}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === 'tarifas'
                  ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <DollarSign size={18} />
              Tarifas
            </button>
          </div>
        </div>

        {/* Contenido de la pestaña */}
        <FormCard>
          <form onSubmit={(event) => event.preventDefault()}>
            {renderTabContent()}
            
            {/* Acciones del formulario */}
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                disabled={isLoading}
                type="button"
              >
                Cancelar
              </Button>
              <Button 
                type="button"
                variant="primary" 
                onClick={handleSave}
                disabled={isLoading || afipBlocked}
              >
                {isLoading ? 'Guardando...' : 'Crear Cliente'}
              </Button>
            </div>
          </form>
        </FormCard>
      </div>
    </>
  );
}

/*
  ============================================================================
  CAMPOS INCLUIDOS EN EL PAYLOAD UNIFICADO (nuevoCliente)
  ============================================================================
  
  PESTAÑA GENERAL:
  - razonSocial: Razón social o nombre legal de la empresa
  - nombreFantasia: Nombre comercial o de fantasía
  - tipoDocumento: Tipo de identificación (CUIT, DNI, Pasaporte)
  - cuit: Número de documento identificatorio
  - condicionIVA: Situación ante IVA (Responsable Inscripto, Monotributista, etc.)
  - categoriaCliente: Segmentación (Mayorista, Minorista, Transporte)
  - direccion: Calle y número de domicilio
  - pisoDepto: Piso y departamento
  - pais: País de ubicación
  - provincia: ID de la provincia seleccionada
  - localidad: ID de la localidad seleccionada
  - codigoLocalidad: Código de la localidad (auto-completado, no editable)
  - codigoPostal: Código postal
  - observaciones: Notas generales del cliente
  
  PESTAÑA CONTACTO:
  - contacto: Nombre de la persona de contacto
  - rolContacto: Cargo o rol de la persona de contacto
  - telefono: Número de teléfono principal
  - celular: Número de celular
  - telefonoAlternativo: Número telefónico adicional
  - emails: Array de direcciones de correo electrónico
  - sitioWeb: URL del sitio web
  - horarioAtencion: Horario preferido para contacto
  - notasContacto: Observaciones del contacto
  - isPrincipal: Indica si es el contacto principal (boolean)
  
  PESTAÑA COMERCIAL:
  - empresa: Empresa asociada
  - sucursal: Sucursal o delegación
  - vendedor: Vendedor asignado
  - tipoCliente: Clasificación del cliente (Normal, VIP, Mayorista, etc.)
  - listaPrecio: Lista de precios aplicable
  - condicionVenta: Condición de pago (Contado, 30/60/90 días, etc.)
  - plazoPago: Cantidad de días de plazo para pago
  - metodoPago: Método de pago preferido (Transferencia, Efectivo, etc.)
  - limiteCredito: Monto máximo de crédito disponible
  - zonaReparto: Zona geográfica de reparto
  - observacionesComerciales: Notas sobre condiciones comerciales especiales
  - activo: Estado del cliente (boolean)
  - fechaAlta: Fecha de alta del cliente
  
  NOTA IMPORTANTE (Refactorización Dic 2024):
  - Se eliminaron los campos 'latitud' y 'longitud' del frontend (no se envían al backend)
  - El campo 'codigoLocalidad' ahora es read-only y se auto-completa al seleccionar la localidad
  - Los campos 'provincia' y 'localidad' ahora envían IDs en lugar de nombres
  
  TOTAL: 42 campos distribuidos en 3 pestañas (antes 44)
  ============================================================================
*/
